/* eslint-disable import/extensions */
/* eslint-disable import/no-named-as-default-member */
/* eslint-disable import/no-named-as-default */
/**
 * @file PatientRecordsView.jsx
 * @author Fernando Ruano
 * @description Patient Records View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Menu } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';
import BloodPressureView from '@nephro/pages/PatientRecords/Components/BloodPressure/bloodPressureRecordsView.js';
import GlucometryView from '@nephro/pages/PatientRecords/Components/Glucometry/glucometryRecordsView.js';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { PATIENT_CONDITIONS } from 'pages/Indicators/helpers/constants';

// Assets
import { ReactComponent as MenuHeartIconGreen } from '@nephro/assets/menuHeartIconGreen.svg';
import { ReactComponent as MenuHeartIconGray } from '@nephro/assets/menuHeartIconGray.svg';
import { ReactComponent as GlucometryIconGreen } from '@nephro/assets/glucometryIconGreen.svg';
import { ReactComponent as GlucometryIconGray } from '@nephro/assets/glucometryIconGray.svg';

// Hooks
import usePatientIndicators from 'pages/Indicators/helpers/usePatientIndicators';

const StyledMenu = styled(Menu)`
  display: flex;
  justify-content: center;
`;

function PatientRecordsView({ bloodPressureActions, glucometryActions }) {
  const [currentTab, setCurrentTab] = React.useState('pressure');
  const [patientData, setPatientData] = React.useState(null);
  const [isDiabetic, setIsDiabetic] = React.useState(false);
  const [isPacient, setIsPacient] = React.useState(true);
  const conditions = usePatientIndicators(PATIENT_CONDITIONS);
  const onChangeTab = (itm) => setCurrentTab(itm.key);
  const { t: translate } = useTranslation();

  const pressureIcon =
    currentTab === 'pressure' ? (
      <MenuHeartIconGreen width={24} height={24} />
    ) : (
      <MenuHeartIconGray width={24} height={24} />
    );
  const glucometryIcon =
    currentTab === 'glucometry' ? (
      <GlucometryIconGreen width={24} height={24} />
    ) : (
      <GlucometryIconGray width={24} height={24} />
    );

  React.useEffect(() => {
    if (conditions.length > 0) {
      const [diabeticItem] = conditions?.filter((itm) => itm.parametro === 25);
      setIsDiabetic(diabeticItem.valor_texto === 'SI');
    }
    const getUserData = async () => {
      const patientInfo = await getStorageItem('patientInfo');
      const adminInfo = await getStorageItem('adminInfo');
      setPatientData(patientInfo);
      setIsPacient(true);
      if (adminInfo) {
        setIsDiabetic(adminInfo.diagnostico_diabetes === 'SI');
        setIsPacient(false);
      }
    };
    getUserData();
  }, [conditions]);

  return (
    <AppCardLayout>
      <div className="h-full records-menu patient-records overflow-y-hidden">
        <StyledMenu
          className="menuStyle"
          onClick={onChangeTab}
          selectedKeys={[currentTab]}
          mode="horizontal">
          <StyledMenu.Item key="pressure" icon={pressureIcon}>
            {translate('dashboard.bloodPressure')}
          </StyledMenu.Item>
          {isDiabetic && (
            <StyledMenu.Item key="glucometry" icon={glucometryIcon}>
              {translate('dashboard.glucometry')}
            </StyledMenu.Item>
          )}
        </StyledMenu>
        <div className="records-menu pt-5 pb-10 overflow-y-auto h-full">
          {currentTab === 'pressure' && patientData && (
            <BloodPressureView
              patientData={patientData}
              bloodPressureActions={bloodPressureActions}
              isPacient={isPacient}
            />
          )}
          {currentTab === 'glucometry' && patientData && (
            <GlucometryView
              patientData={patientData}
              glucometryActions={glucometryActions}
              isPacient={isPacient}
            />
          )}
        </div>
      </div>
    </AppCardLayout>
  );
}

/**
 * Component definition of proptypes.
 */
PatientRecordsView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  bloodPressureActions: PropTypes.shape({
    getBloodPressuresArray: PropTypes.func.isRequired,
    createBloodPressure: PropTypes.func.isRequired,
    getBloodPressureById: PropTypes.func.isRequired,
    updateBloodPressure: PropTypes.func.isRequired,
    deleteBloodPressureById: PropTypes.func.isRequired
  }).isRequired,
  glucometryActions: PropTypes.shape({
    getGlucometryByPatientId: PropTypes.func.isRequired,
    createGlucometry: PropTypes.func.isRequired,
    getGlucometryById: PropTypes.func.isRequired,
    updateGlucometry: PropTypes.func.isRequired,
    deleteGlucometryById: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default PatientRecordsView;
